// 各平台的發售日（GDO-23）：同一款遊戲在不同平台的日期可能不同（例如先在 PC 發售、一年後推出 Switch 2 版）。
// 依使用者選的平台與要看的狀態（未發售／已發售），決定這款遊戲在清單上用哪個日期。前端與分片判定共用。
import { PLATFORMS } from './dictionaries.ts';
import {
  compareReleaseDate,
  type GameStatus,
  isReleased,
  parseReleaseDate,
  type ReleaseDate,
  releaseSortKey,
} from './release-date.ts';
import type { IndexRow } from './snapshot.ts';

export type ReleaseRow = Pick<IndexRow, 'd' | 'p' | 'st'> & Partial<Pick<IndexRow, 'pd'>>;

/**
 * 每個平台（PLATFORMS 的索引）的發售日：預設是主日期，列在 pd 的平台用自己的日期。
 * 沒有平台資料時回傳一筆主日期（平台索引 -1）。日期格式不符的略過。
 */
export function platformReleases(row: ReleaseRow): { platform: number; date: ReleaseDate }[] {
  const main = parseReleaseDate(row.d);
  const own = new Map(row.pd ?? []);
  const out: { platform: number; date: ReleaseDate }[] = [];
  for (let i = 0; i < PLATFORMS.length; i++) {
    const listed = own.has(i);
    if (!listed && !(row.p & (1 << i))) continue;
    const date = listed ? parseReleaseDate(own.get(i)) : main;
    if (date) out.push({ platform: i, date });
  }
  if (out.length === 0 && main) out.push({ platform: -1, date: main });
  return out;
}

export interface ReleaseView {
  /** 要看未發售（含今天發售） */
  up: boolean;
  /** 要看已發售 */
  rel: boolean;
}

const isReleaseDay = (date: ReleaseDate, today: number) => date.p === 'day' && releaseSortKey(date) === today;

const earliest = (dates: ReleaseDate[]) =>
  dates.reduce<ReleaseDate | null>((best, d) => (!best || compareReleaseDate(d, best) < 0 ? d : best), null);

/**
 * 這款遊戲在目前的篩選下用哪個日期：
 * - 有選平台時只看這些平台的日期（沒選 = 所有平台）
 * - 要看未發售、且有還沒發售（或今天發售）的平台 → 取其中最早的（例如新平台版的日期）
 * - 否則要看已發售、且有已發售的平台 → 取其中最早的（原始發售日）
 * - 都不符合 → null（不顯示）
 */
export function releaseForView(
  row: ReleaseRow,
  platforms: readonly number[] | null,
  view: ReleaseView,
  today: number,
): ReleaseDate | null {
  const wanted = platforms?.length ? new Set(platforms) : null;
  const upcoming: ReleaseDate[] = [];
  const released: ReleaseDate[] = [];
  for (const { platform, date } of platformReleases(row)) {
    if (wanted && platform >= 0 && !wanted.has(platform)) continue;
    const done = isReleased(date, row.st as GameStatus, today);
    if (!done || isReleaseDay(date, today)) upcoming.push(date);
    if (done) released.push(date);
  }
  if (view.up && upcoming.length) return earliest(upcoming);
  if (view.rel && released.length) return earliest(released);
  return null;
}

/** 至少有一個平台還沒發售：這款遊戲要放在未發售分片（已經在其他平台發售也一樣） */
export function hasUnreleasedPlatform(row: ReleaseRow, today: number): boolean {
  return platformReleases(row).some(({ date }) => !isReleased(date, row.st as GameStatus, today));
}
